import React, { useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import "react-moment";
import moment from "moment";
import ReactTable from "react-table";
import matchSorter from "match-sorter";
import PropTypes from "prop-types";
import './MovimientosConAI.css'
import ModalConfirmacionCompra from './ModalConfirmacionCompra';
import ResumenComprasSunat from './ResumenComprasSunat';

const PRIMARY_COLOR = "rgb(53, 96, 90)";
const SECONDARY_COLOR = "rgb(1, 165, 156)";
const LIGHT_COLOR = "rgba(53, 96, 90, 0.08)";

const TIPO_DOC_MAP = {
    "01": "FACTURA",
    "03": "BOLETA",
    "07": "NOTA DE CRÉDITO",
    "08": "NOTA DE DÉBITO",
    "09": "GUÍA DE REMISIÓN"
};

const obtenerTipoDocDescripcion = (codigo) => {
    return TIPO_DOC_MAP[codigo] || codigo;
};

const MovimientosConAI = ({ usuario_id = 1, empresa_id = 1 }) => {
    const history = useHistory();
    
    const [periodo, setPeriodo] = useState(moment().format("YYYY-MM"));
    const [compras, setCompras] = useState([]);
    const [loadingCompras, setLoadingCompras] = useState(false);
    const [errorCompras, setErrorCompras] = useState("");
    
    const [mostrarModalConfirmacion, setMostrarModalConfirmacion] = useState(false);
    const [compraSeleccionada, setCompraSeleccionada] = useState(null);

    const convertirPeriodo = useCallback((mesFormato) => {
        if (!mesFormato || mesFormato.length !== 7) return null;
        return mesFormato.replace("-", "");
    }, []);

    const verificarComprasRegistradas = useCallback(async (comprasDelaSunat) => {
        try {
            const comprobantesAVerificar = comprasDelaSunat.map(c => ({
                nroDocumentoSunat: `${c.serie}-${c.numero}`,
                idTipoDocumentoSunat: c.tipo_doc
            }));

            const response = await fetch(`/api/movimientosalmacen/verificar-comprobantes`, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ comprobantes: comprobantesAVerificar })
            });

            if (!response.ok) {
                console.warn("No se pudo verificar compras registradas, asumiendo todas como pendientes");
                return comprasDelaSunat.map(c => ({
                    ...c,
                    estadoProcesamiento: "Pendiente",
                    yaRegistrado: false
                }));
            }

            const datos = await response.json();
            const registrados = datos.registrados || [];

            return comprasDelaSunat.map(c => {
                const nroDocSunat = `${c.serie}-${c.numero}`;
                const estáRegistrado = registrados.includes(nroDocSunat);
                
                return {
                    ...c,
                    estadoProcesamiento: estáRegistrado ? "Procesado" : "Pendiente",
                    yaRegistrado: estáRegistrado
                };
            });
        } catch (error) {
            console.error("Error verificando compras registradas:", error);
            return comprasDelaSunat.map(c => ({
                ...c,
                estadoProcesamiento: "Pendiente",
                yaRegistrado: false
            }));
        }
    }, []);

    const consultarComprasSunat = useCallback(async () => {
        const periodoConvertido = convertirPeriodo(periodo);
        
        if (!periodoConvertido) {
            setErrorCompras("Formato de período inválido");
            return;
        }

        setLoadingCompras(true);
        setErrorCompras("");

        try {
            const response = await fetch(`/api/sire/sunat/compras/${periodoConvertido}`);
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data.message || "Error al consultar SUNAT");
            }

            let comprasData = (data.data || data.compras || []).map(compra => ({
                ...compra,
                tipo_doc_descripcion: obtenerTipoDocDescripcion(compra.tipo_doc)
            }));

            comprasData = await verificarComprasRegistradas(comprasData);

            setCompras(comprasData);
        } catch (error) {
            console.error("Error consultando SUNAT:", error);
            setErrorCompras(error.message || "Error al conectar con SUNAT");
            setCompras([]);
        } finally {
            setLoadingCompras(false);
        }
    }, [periodo, convertirPeriodo, verificarComprasRegistradas]);

    const handleRegistrarClick = useCallback((compra) => {
        setCompraSeleccionada(compra);
        setMostrarModalConfirmacion(true);
    }, []);

    const handleCerrarModalConfirmacion = useCallback(() => {
        setMostrarModalConfirmacion(false);
        setCompraSeleccionada(null);
    }, []);

    const handleConfirmarCompra = useCallback((compra) => {
        setMostrarModalConfirmacion(false);
        history.push('/movimientosalmacen', { compraDelaSunat: compra, skipSunatValidation: true });
    }, [history]);

    const formatearMoneda = useCallback((valor, moneda) => {
        const safeCurrency = moneda === "USD" ? "USD" : "PEN";
        const num = Number(valor || 0);

        return new Intl.NumberFormat("es-PE", {
            style: "currency",
            currency: safeCurrency,
            minimumFractionDigits: 2,
        }).format(num);
    }, []);

    const renderTablaCompras = () => {
        const columns = [
            {
                columns: [
                    {
                        Header: "Fecha",
                        id: "fecha_emision",
                        accessor: d => d.fecha_emision,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["fecha_emision"] }),
                        filterAll: true,
                        width: 120
                    },
                    {
                        Header: "Tipo Doc",
                        id: "tipo_doc_descripcion",
                        accessor: d => d.tipo_doc_descripcion,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["tipo_doc_descripcion"] }),
                        filterAll: true,
                        width: 140
                    },
                    {
                        Header: "Serie",
                        id: "serie",
                        accessor: d => d.serie,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["serie"] }),
                        filterAll: true,
                        width: 100
                    },
                    {
                        Header: "Número",
                        id: "numero",
                        accessor: d => d.numero,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["numero"] }),
                        filterAll: true,
                        width: 130
                    },
                    {
                        Header: "RUC Proveedor",
                        id: "ruc_proveedor",
                        accessor: d => d.ruc_proveedor,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["ruc_proveedor"] }),
                        filterAll: true,
                        width: 140
                    },
                    {
                        Header: "Razón Social",
                        id: "razon_social",
                        accessor: d => d.razon_social,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["razon_social"] }),
                        filterAll: true
                    },
                    {
                        Header: "Total",
                        id: "total",
                        accessor: d => Number(d.total || 0),
                        Cell: props => <div className="text-right">{formatearMoneda(props.original.total, props.original.moneda)}</div>,
                        width: 130
                    },
                    {
                        Header: "Moneda",
                        id: "moneda",
                        accessor: d => d.moneda,
                        filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ["moneda"] }),
                        filterAll: true,
                        width: 100
                    },
                    {
                        Header: "Estado",
                        id: "estadoProcesamiento",
                        accessor: d => d.estadoProcesamiento || "Pendiente",
                        Cell: props => {
                            const estado = props.original.estadoProcesamiento || "Pendiente";
                            const esProcessado = estado === "Procesado";
                            const badgeColor = esProcessado ? "#28a745" : "#ffc107";
                            const textColor = esProcessado ? "#fff" : "#000";
                            
                            return (
                                <span 
                                    style={{
                                        backgroundColor: badgeColor,
                                        color: textColor,
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        fontWeight: "bold",
                                        fontSize: "0.85rem"
                                    }}
                                >
                                    {estado}
                                </span>
                            );
                        },
                        width: 120
                    },
                    {
                        Header: "Acción",
                        id: "accion",
                        accessor: d => d,
                        Cell: props => {
                            const compra = props.original;
                            const esProcessado = compra.yaRegistrado === true;
                            
                            return (
                                <button
                                    className="btn btn-sm"
                                    style={{
                                        backgroundColor: esProcessado ? "#ccc" : SECONDARY_COLOR,
                                        color: esProcessado ? "#666" : "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        padding: "6px 10px",
                                        cursor: esProcessado ? "not-allowed" : "pointer"
                                    }}
                                    onClick={() => handleRegistrarClick(compra)}
                                    disabled={esProcessado}
                                    title={esProcessado ? "Esta compra ya ha sido registrada" : "Abrir formulario de ingreso de almacén con datos de esta compra"}
                                >
                                    <i className={`fas ${esProcessado ? "fa-check" : "fa-plus"} me-1`}></i>
                                    {esProcessado ? "Registrado" : "Registrar"}
                                </button>
                            );
                        },
                        sortable: false,
                        filterable: false,
                        width: 150
                    }
                ]
            }
        ];

        if (!compras.length) {
            return (
                <div className="text-center py-5">
                    <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                    <p className="text-muted">No se encontraron compras para el período seleccionado</p>
                </div>
            );
        }

        return (
            <div className="py-3 reporte-compras-sire-root">
                <div className="card border-0 shadow-sm">
                    <div
                        className="card-header d-flex justify-content-between align-items-center"
                        style={{
                            background: `linear-gradient(90deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
                            color: "#fff",
                        }}
                    >
                        <span className="font-weight-bold">
                            <i className="fas fa-receipt me-2"></i>
                            Detalle de Compras SUNAT
                        </span>
                        <span>{compras.length} compras</span>
                    </div>
                    <div className="card-body p-2" style={{ backgroundColor: "transparent" }}>
                        <ReactTable
                            data={compras}
                            filterable
                            columns={columns}
                            defaultPageSize={10}
                            className="-striped -highlight"
                            previousText="Anterior"
                            nextText="Siguiente"
                            loadingText="Cargando..."
                            noDataText="No se encontraron registros"
                            pageText="Página"
                            ofText="de"
                            rowsText="filas"
                            pageSizeOptions={[10, 20, 25, 50, 100]}
                            minRows={1}
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <section className="ventas-fpay-section-vh">
            <ModalConfirmacionCompra
                isOpen={mostrarModalConfirmacion}
                compra={compraSeleccionada}
                onClose={handleCerrarModalConfirmacion}
                onConfirmar={handleConfirmarCompra}
            />
            
            <div className="container-fluid px-md-5">
                <div className="mb-2 pt-3">
                    <h4 className="mt-2 text-center mb-4">
                        <i className="fas fa-sync-alt me-2" style={{ color: SECONDARY_COLOR }}></i>
                        Movimientos de Compras - Reconciliación SUNAT
                    </h4>

                    <div className="alert alert-info" role="alert">
                        <i className="fas fa-info-circle me-2"></i>
                        <strong>Información:</strong> Los datos que se muestran provienen directamente de SUNAT SIRE. 
                        Puedes registrar automáticamente en tu sistema interno aquellas compras que desees.
                    </div>

                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="row align-items-end">
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">
                                        <i className="fas fa-calendar-alt me-1"></i>
                                        Seleccionar Período
                                    </label>
                                    <input
                                        type="month"
                                        className="form-control"
                                        value={periodo}
                                        onChange={(e) => setPeriodo(e.target.value)}
                                        disabled={loadingCompras}
                                    />
                                </div>
                                <div className="col-md-8">
                                    <button
                                        className="btn w-100"
                                        style={{ backgroundColor: SECONDARY_COLOR, color: "white" }}
                                        onClick={consultarComprasSunat}
                                        disabled={loadingCompras}
                                    >
                                        {loadingCompras ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Consultando SUNAT...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-search me-2"></i>
                                                Consultar Compras SUNAT
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {errorCompras && (
                        <div className="alert alert-danger" role="alert">
                            <i className="fas fa-exclamation-circle me-2"></i>
                            {errorCompras}
                        </div>
                    )}

                    {compras.length === 0 && !loadingCompras && !errorCompras ? (
                        <div className="text-center py-5">
                            <i className="fas fa-search fa-3x text-muted mb-3"></i>
                            <p className="text-muted">
                                Selecciona un período y consulta los datos de SUNAT para comenzar
                            </p>
                        </div>
                    ) : (
                        renderTablaCompras()
                    )}

                    <ResumenComprasSunat compras={compras} />

                    <div className="mt-4 text-center text-muted small">
                        <i className="fas fa-clock me-1"></i>
                        Última actualización: {moment().format("DD/MM/YYYY HH:mm")}
                    </div>
                </div>
            </div>
        </section>
    );
};

MovimientosConAI.propTypes = {
    usuario_id: PropTypes.number,
    empresa_id: PropTypes.number
};

MovimientosConAI.defaultProps = {
    usuario_id: 1,
    empresa_id: 1
};

export default MovimientosConAI;
